import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AccountSettingsForm from './components/AccountSettingsForm';
import PaymentMethodCard from './components/PaymentMethodCard';
import CommunicationHistoryItem from './components/CommunicationHistoryItem';
import PrivacyPolicy from '../privacy-policy';
import { useAuth } from '../../contexts/AuthContext';
import { customerPortalService } from '../../services/customerPortalService';

const EMPTY_USER_DATA = {
  fullName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  customerType: 'individual',
  companyName: '',
  siret: '',
  emailNotifications: true,
  smsNotifications: true,
  autoReminders: true,
  promotionalEmails: false,
};

const ACCOUNT_SECTIONS = [
  { id: 'communications', label: 'Communications', icon: 'MessageSquare' },
  { id: 'payments', label: 'Moyens de paiement', icon: 'CreditCard' },
  { id: 'settings', label: 'Mon profil', icon: 'User' },
  { id: 'privacy-policy', label: 'Politique de confidentialité', icon: 'Shield' },
];

const VALID_SECTIONS = new Set(ACCOUNT_SECTIONS.map((s) => s.id));

const CustomerAccountPortal = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile, signOut, refreshProfile } = useAuth();

  const sectionFromUrl = searchParams.get('section');
  const initialSection = VALID_SECTIONS.has(sectionFromUrl) ? sectionFromUrl : 'settings';

  const [activeSection, setActiveSection] = useState(initialSection);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [userData, setUserData] = useState(EMPTY_USER_DATA);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [communications, setCommunications] = useState([]);

  const loadPortalData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError('');
      const data = await customerPortalService.getPortalData(user, profile);
      setUserData(data.userData);
      setPaymentMethods(data.paymentMethods);
      setCommunications(data.communications);
    } catch (err) {
      console.error('Erreur chargement portail client:', err);
      setError(err?.message || 'Impossible de charger vos données');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  useEffect(() => {
    const next = VALID_SECTIONS.has(sectionFromUrl) ? sectionFromUrl : 'settings';
    setActiveSection(next);
  }, [sectionFromUrl]);

  const handleLogout = async () => {
    try {
      sessionStorage.setItem('postLogoutRedirect', '/');
      await signOut();
    } catch (err) {
      sessionStorage.removeItem('postLogoutRedirect');
      console.error('Erreur déconnexion:', err);
    }
    navigate('/', { replace: true });
  };

  const handleViewDetails = () => {
    navigate('/public-service-tracking');
  };

  const handleSelectSection = (sectionId) => {
    setActiveSection(sectionId);
    setSearchParams({ section: sectionId }, { replace: true });
  };

  const handleSaveSettings = async (data) => {
    if (!user?.id) {
      throw new Error('Vous devez être connecté pour modifier votre profil');
    }

    setSaveMessage('');
    setError('');

    const result = await customerPortalService.updateUserSettings(user.id, data, user);
    const updatedProfile = await refreshProfile(user.id);
    const freshUser = result?.user || user;

    const portalData = await customerPortalService.getPortalData(
      freshUser,
      updatedProfile || profile
    );

    setUserData(portalData.userData);
    setSaveMessage('Profil mis à jour avec succès');
  };

  const handleSavePassword = async (password) => {
    if (!user?.id) {
      throw new Error('Vous devez être connecté pour modifier votre mot de passe');
    }
    setError('');
    await customerPortalService.updateOwnPassword(password);
  };

  const renderEmptyState = (message) => (
    <div className="bg-card rounded-lg border border-border p-8 text-center">
      <Icon name="Inbox" size={40} className="text-muted-foreground mx-auto mb-3" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Chargement de votre espace client...</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'communications':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Communications
              </h1>
              <p className="text-muted-foreground">
                Consultez tous vos échanges avec notre service client
              </p>
            </div>
            <div className="space-y-4">
              {communications?.length > 0
                ? communications.map((communication) => (
                    <CommunicationHistoryItem
                      key={communication?.id}
                      communication={communication}
                      onViewDetails={handleViewDetails}
                    />
                  ))
                : renderEmptyState('Aucune communication enregistrée')}
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Moyens de paiement
              </h1>
              <p className="text-muted-foreground">
                Gérez vos cartes bancaires et prélèvements SEPA
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {paymentMethods?.length > 0
                ? paymentMethods.map((method) => (
                    <PaymentMethodCard
                      key={method?.id}
                      method={method}
                      isDefault={method?.isDefault}
                      onSetDefault={() => {}}
                      onRemove={() => {}}
                      onEdit={() => {}}
                    />
                  ))
                : renderEmptyState('Aucun moyen de paiement enregistré')}
            </div>
            <Button variant="outline" iconName="Plus" iconPosition="left">
              Ajouter un moyen de paiement
            </Button>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Mon profil
              </h1>
              <p className="text-muted-foreground">
                Modifiez vos informations personnelles et préférences
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-4 sm:p-5">
              {saveMessage && (
                <div className="mb-4 p-3 rounded-lg border border-success/30 bg-success/10 text-success text-sm">
                  {saveMessage}
                </div>
              )}
              <AccountSettingsForm
                initialData={userData}
                onSave={handleSaveSettings}
                onSavePassword={handleSavePassword}
              />
            </div>
          </div>
        );

      case 'privacy-policy':
        return (
          <div className="max-w-7xl mx-auto">
            <PrivacyPolicy />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole={profile?.role || 'client'}
        userName={userData?.fullName?.trim() || profile?.full_name || 'Client'}
        onLogout={handleLogout}
      />

      <div className="main-content">
        <div className="flex">
          <aside className="hidden lg:block w-64 shrink-0 sticky top-16 h-[calc(100vh-4rem)] bg-card border-r border-border overflow-y-auto">
            <div className="p-4">
              <nav className="space-y-1">
                {ACCOUNT_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleSelectSection(section.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                      transition-all duration-200
                      ${
                        activeSection === section.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Icon name={section.icon} size={20} />
                    <span>{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="px-2 sm:px-4 lg:px-6 py-4 lg:py-6 max-w-7xl mx-auto">
              {error && (
                <div className="mb-6 p-4 rounded-lg border border-error/30 bg-error/10 text-error text-sm">
                  {error}
                </div>
              )}
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default CustomerAccountPortal;
